import React, { useState, useEffect, useRef, forwardRef, useImperativeHandle } from 'react';
import PropTypes from 'prop-types';
import styles from './App.css';
import WarningModal from '../../5_atoms/warningModal/WarningModal';
import * as timeoutModalTypes from '../../constants/timeoutModalTypes';
import { fetchRefreshedToken } from '../../api/tokenAPI';

const App = forwardRef(function App(props, ref) {
    const tokenRef = useRef(props.token);
    const brokerConfigIdRef = useRef(props.brokerConfigId);
    const sessionTimeoutCallbackRef = useRef(props.sessionTimeoutCallback);
    const updateTokenCallbackRef = useRef(props.updateTokenCallback);
    const warningTimeoutIdRef = useRef(null);
    const sessionTimeoutIdRef = useRef(null);

    const [modalVisible, setModalVisible] = useState(false);
    const [modalType, setModalType] = useState(null);

    useEffect(() => {
        // An optional handler that is executed when the component has rendered
        props.onInitialized?.();
    }, [props.onInitialized]);

    useEffect(() => {
        setTimeouts();

        return () => {
            // Cleanup
            clearTimeouts();
        };
    }, []);

    useImperativeHandle(ref, () => {
        return {
            setSessionTimeoutCallback: (callback) => {
                sessionTimeoutCallbackRef.current = callback;
            },
            setUpdateTokenCallback: (callback) => {
                updateTokenCallbackRef.current = callback;
            },
            resetToken: (brokerConfigId) => {
                if (brokerConfigId) {
                    brokerConfigIdRef.current = brokerConfigId;
                }

                updateToken();
            }
        };
    }, []);

    function showModal(visible, type = null) {
        setModalVisible(visible);
        setModalType(type);
    }

    function clearTimeouts() {
        if (warningTimeoutIdRef.current) {
            clearTimeout(warningTimeoutIdRef.current);
        }

        if (sessionTimeoutIdRef.current) {
            clearTimeout(sessionTimeoutIdRef.current);
        }
    }

    function setTimeouts() {
        warningTimeoutIdRef.current = setTimeout(() => {
            showModal(true, timeoutModalTypes.WARNING);
        }, props.warningTimeout);

        sessionTimeoutIdRef.current = setTimeout(() => {
            showModal(true, timeoutModalTypes.SESSION);
        }, props.sessionTimeout);
    }

    async function updateToken() {
        clearTimeouts();

        const tokenResponse = await fetchRefreshedToken(props.apiDomain, brokerConfigIdRef.current, tokenRef.current);

        if (!tokenResponse || tokenResponse.IsSessionExpired) {
            showModal(true, timeoutModalTypes.SESSION);
        }
        else {
            tokenRef.current = tokenResponse.TokenString;

            setTimeouts();

            updateTokenCallbackRef.current?.(tokenRef.current);
        }
    }

    function handleWarningModalClick() {
        showModal(false);
        updateToken();
    }

    function handleSessionModalClick() {
        sessionTimeoutCallbackRef.current?.();
    }

    return (
        <div className={styles.container}>
            {modalVisible && (modalType === timeoutModalTypes.WARNING ?
                <WarningModal type={timeoutModalTypes.WARNING} onClick={handleWarningModalClick} startTimeInSeconds={(props.sessionTimeout - props.warningTimeout) / 1000} /> :
                <WarningModal type={timeoutModalTypes.SESSION} onClick={handleSessionModalClick} />)
            }
        </div>
    );
});

App.propTypes = {
    token: PropTypes.string,
    brokerConfigId: PropTypes.string,
    sessionTimeoutCallback: PropTypes.func,
    updateTokenCallback: PropTypes.func,
    warningTimeout: PropTypes.number, // Warning timeout value in milliseconds
    sessionTimeout: PropTypes.number, // Session timeout value in milliseconds
    apiDomain: PropTypes.string,
    onInitialized: PropTypes.func
};

export default App;